import type { Accessor, JSX, ParentComponent, VoidComponent } from "solid-js";
import { createEffect, createMemo, createSignal, For, onMount, Show, splitProps, untrack } from "solid-js";
import type { IconName } from "../icon/Icon";
import { Icon } from "../icon/Icon";
import type { Editor } from "@tiptap/core";
import { isNodeSelection } from "@tiptap/core";
import { useWysiwyg } from "./WysiwygContext";
import { useTranslation } from "../../state/i18n";
import { ColorPickerContext, Menu, Popover } from "@ark-ui/solid";
import { noop } from "../../utils/noop";
import ColorPicker from "../colorPicker/ColorPicker";
import { clamp, isEmpty, isNil, times } from "lodash-es";
import { IconButton, TextAndIconButton, TextButton } from "../button/Button";
import { createPositionToElement, useMousePosition } from "@solid-primitives/mouse";
import { useKeyDownEvent } from "@solid-primitives/keyboard";
import InputModal from "../modal/InputModal";
import type { InferOutput } from "valibot";
import { boolean, nonOptional, object, optional, pipe, string, url } from "valibot";
import { TextInput } from "../inputs/TextInput";
import { errorTranslation } from "../../utils/validation/base";
import { Checkbox } from "../checkbox/Checkbox";
import { BubbleMenu } from "./tiptap/BubbleMenu";
import logger from "../../utils/logger";
import MediaModal from "../modal/MediaModal";
import CustomerResourceService, {
  type CustomerResource,
  type PendingCustomerResource,
} from "../../services/CustomerResourceService";
import parse from "inline-style-parser";
import type { TextListItemProps } from "../menu/listItems/TextListItem";
import TextListItem from "../menu/listItems/TextListItem";
import { useModalManager } from "../modal/ModalManager";
import { keyFromSelector } from "i18next";
import SeparatorLine from "../SeparatorLine";

type ToolbarButtonProps = {
  disableOverride?: boolean;
  isActive?: (editor: Accessor<Editor>) => boolean;
  onClick: (editor: Accessor<Editor>) => void;
} & Omit<JSX.HTMLAttributes<HTMLButtonElement>, "onClick">;
const ToolbarButton: ParentComponent<ToolbarButtonProps> = (props) => {
  const [localProps, otherProps] = splitProps(props, ["children", "disableOverride", "isActive", "onClick"]);

  const { editor, mode } = useWysiwyg();
  const isDisabled = createMemo(() => localProps.disableOverride ?? mode() === "code");
  const isActive = createMemo(() => localProps.isActive?.(editor) ?? false);

  return (
    <button
      class="flex items-center gap-1.5 rounded-sm p-1.5 text-button-small focus-visible:outline-default"
      classList={{
        "bg-button-bg-tertiary-default text-button-text-tertiary-harsh [&>svg]:icon-button-icon-tertiary-harsh":
          !isDisabled(),
        "pointer-events-none bg-button-bg-tertiary-default text-button-text-tertiary-disabled [&>svg]:icon-button-icon-tertiary-disabled":
          isDisabled(),
        "hover:bg-button-bg-tertiary-hover": !isDisabled() && !isActive(),
        "bg-button-bg-tertiary-active": !isDisabled() && isActive(),
      }}
      onClick={() => localProps.onClick(editor)}
      disabled={isDisabled()}
      title={props["aria-label"]}
      {...otherProps}
    >
      {localProps.children}
    </button>
  );
};

type CommandListItem = { key: string } & Omit<TextListItemProps, "htmlProps">;
type BaseSelectCommandProps<T> = {
  items: CommandListItem[];
  run: (editor: Accessor<Editor>, value: T) => void;
  "aria-label": string;
} & ({ text: string; icon?: never } | { text?: never; icon: IconName });
const BaseSelectCommand = <T,>(props: BaseSelectCommandProps<T>) => {
  const { editor } = useWysiwyg();

  return (
    <Menu.Root
      positioning={{
        placement: "bottom-end",
      }}
      closeOnSelect
      onSelect={({ value }) => {
        props.run(editor, value as T);
      }}
    >
      <Menu.Trigger
        asChild={(triggerProps) => (
          <ToolbarButton
            onClick={noop}
            isActive={() => false}
            aria-label={props.text ? undefined : props["aria-label"]}
            title={props["aria-label"]}
            {...triggerProps}
          >
            <Show when={props.text}>
              {(text) => (
                <span>
                  <span class="sr-only">{props["aria-label"]}: </span>
                  {text()}
                </span>
              )}
            </Show>
            <Show when={props.icon}>{(icon) => <Icon name={icon()} type="solid" size="md" />}</Show>
            <Icon name="chevronDown" type="solid" size="sm" />
          </ToolbarButton>
        )}
      />
      <Menu.Positioner>
        <Menu.Content class="z-10 -my-1 max-h-86 max-w-64 min-w-42 overflow-y-auto rounded-sm bg-surface-default p-1 shadow-elevation-light-3 outline-none">
          <For each={props.items}>
            {(item) => (
              <Menu.Item value={item.key} asChild={(arkProps) => <TextListItem htmlProps={arkProps()} {...item} />} />
            )}
          </For>
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  );
};

type BaseButtonCommandProps = {
  icon: IconName;
  disableOverride?: boolean;
  active: (editor: Accessor<Editor>) => boolean;
  run: (editor: Accessor<Editor>) => void;
} & Omit<JSX.HTMLAttributes<HTMLButtonElement>, "onClick">;
const BaseButtonCommand: VoidComponent<BaseButtonCommandProps> = (props) => {
  const [localProps, otherProps] = splitProps(props, ["icon", "disableOverride", "active", "run", "aria-label"]);

  return (
    <ToolbarButton
      {...otherProps}
      onClick={localProps.run}
      isActive={localProps.active}
      disableOverride={localProps.disableOverride}
      aria-label={localProps["aria-label"]}
    >
      <Icon name={localProps.icon} type="solid" size="md" />
    </ToolbarButton>
  );
};

type FontFamilyItem = {
  key: string;
  name: string;
};

export const FontFamilyCommand = () => {
  const { t } = useTranslation();
  const { editor } = useWysiwyg();

  const defaultFontFamily = { key: "arial", name: "Arial" };
  const fontFamilies: FontFamilyItem[] = [
    defaultFontFamily,
    { key: "arial black", name: "Arial Black" },
    { key: "comic sans ms", name: "Comic Sans MS" },
    { key: "courier new", name: "Courier New" },
    { key: "helvetica neue", name: "Helvetica Neue" },
    { key: "helvetica", name: "Helvetica" },
    { key: "impact", name: "Impact" },
    { key: "lucida grande", name: "Lucida Grande" },
    { key: "tahoma", name: "Tahoma" },
    { key: "times new roman", name: "Times New Roman" },
    { key: "verdana", name: "Verdana" },
  ];

  const [selectedFontFamily, setSelectedFontFamily] = createSignal<FontFamilyItem>(defaultFontFamily);

  createEffect(() => {
    const fontFamily = editor().getAttributes("textStyle").fontFamily;
    setSelectedFontFamily(fontFamilies.find((item) => item.key === fontFamily) ?? defaultFontFamily);
  });

  return (
    <BaseSelectCommand<string>
      aria-label={t(($) => $.WYSIWYG_COMMAND_FONT_FAMILY_LABEL)}
      text={selectedFontFamily().name}
      run={(editor, value) => {
        editor().chain().focus().setFontFamily(value).run();
      }}
      items={fontFamilies.map(
        ({ key, name }) =>
          ({
            key,
            label: name,
            labelStyle: { "font-family": name },
          }) satisfies CommandListItem,
      )}
    />
  );
};

type TextType =
  | "paragraph"
  | "heading-1"
  | "heading-2"
  | "heading-3"
  | "heading-4"
  | "heading-5"
  | "heading-6"
  | "blockquote"
  | "code-block";

export const TextTypeCommand = () => {
  const { t } = useTranslation();
  const { editor } = useWysiwyg();

  const items = {
    "heading-1": {
      label: t(($) => $.WYSIWYG_COMMAND_TEXT_TYPE_OPTION_HEADING_1),
      class: "text-header-3xl",
    },
    "heading-2": {
      label: t(($) => $.WYSIWYG_COMMAND_TEXT_TYPE_OPTION_HEADING_2),
      class: "text-header-2xl",
    },
    "heading-3": {
      label: t(($) => $.WYSIWYG_COMMAND_TEXT_TYPE_OPTION_HEADING_3),
      class: "text-header-xl",
    },
    "heading-4": {
      label: t(($) => $.WYSIWYG_COMMAND_TEXT_TYPE_OPTION_HEADING_4),
      class: "text-header-lg",
    },
    "heading-5": {
      label: t(($) => $.WYSIWYG_COMMAND_TEXT_TYPE_OPTION_HEADING_5),
      class: "text-header-md",
    },
    "heading-6": {
      label: t(($) => $.WYSIWYG_COMMAND_TEXT_TYPE_OPTION_HEADING_6),
      class: "text-header-sm",
    },
    paragraph: {
      label: t(($) => $.WYSIWYG_COMMAND_TEXT_TYPE_OPTION_PARAGRAPH),
      class: "text-base-regular",
    },
    blockquote: {
      label: t(($) => $.WYSIWYG_COMMAND_TEXT_TYPE_OPTION_BLOCKQUOTE),
      class: "border-l border-subtle pl-1 text-base-regular",
    },
    "code-block": {
      label: t(($) => $.WYSIWYG_COMMAND_TEXT_TYPE_OPTION_CODE_BLOCK),
      class: "block w-full border border-subtle bg-surface-extra-subtle p-1 text-base-medium font-fira",
    },
  } as const;

  const [textType, setTextType] = createSignal<TextType>("paragraph");

  createEffect(() => {
    const node = editor().state.selection.$from.parent;
    if (node.type.name === "heading") {
      const { level } = node.attrs;
      setTextType(`heading-${level}` as TextType);
    } else if (editor().isActive("blockquote")) {
      setTextType("blockquote");
    } else if (node.type.name === "codeBlock") {
      setTextType("code-block");
    } else {
      setTextType("paragraph");
    }
  });

  return (
    <BaseSelectCommand<TextType>
      aria-label={t(($) => $.WYSIWYG_COMMAND_TEXT_TYPE_LABEL)}
      text={items[textType()].label}
      run={(editor, value) => {
        const chain = editor().chain().focus();

        if (value === "paragraph") {
          chain.setParagraph().run();
        } else if (value === "blockquote") {
          editor().isActive("blockquote") ? chain.unsetBlockquote().run() : chain.setParagraph().setBlockquote().run();
        } else if (value === "code-block") {
          chain.toggleCodeBlock().run();
        } else if (value === "heading-1") {
          chain.toggleHeading({ level: 1 }).run();
        } else if (value === "heading-2") {
          chain.toggleHeading({ level: 2 }).run();
        } else if (value === "heading-3") {
          chain.toggleHeading({ level: 3 }).run();
        } else if (value === "heading-4") {
          chain.toggleHeading({ level: 4 }).run();
        } else if (value === "heading-5") {
          chain.toggleHeading({ level: 5 }).run();
        } else if (value === "heading-6") {
          chain.toggleHeading({ level: 6 }).run();
        }
      }}
      items={Object.entries(items).map(
        ([key, props]) =>
          ({
            key,
            label: props.label,
            labelClass: props.class,
          }) satisfies CommandListItem,
      )}
    />
  );
};

export const TextColorCommand = () => {
  const defaultColor = "#000000";
  const { t } = useTranslation();
  const { editor } = useWysiwyg();
  const [color, setColor] = createSignal<string>(defaultColor);

  createEffect(() => {
    const { color } = editor().getAttributes("textStyle");
    setColor(isEmpty(color) ? defaultColor : color);
  });

  return (
    <ColorPicker
      onChange={(value) => setTimeout(() => editor().chain().focus().setColor(value).run())}
      value={color()}
      showOpacity
      Trigger={(triggerProps) => (
        <ColorPickerContext>
          {(context) => (
            <ToolbarButton
              {...triggerProps}
              onClick={() => context().setOpen(!context().open)}
              aria-label={t(($) => $.WYSIWYG_COMMAND_COLOR_ARIA_LABEL)}
            >
              <Icon name="textColor" type="outline" size="md" />
              <Icon name="chevronDown" type="solid" size="sm" />
            </ToolbarButton>
          )}
        </ColorPickerContext>
      )}
    />
  );
};

export const TextBackgroundColorCommand = () => {
  const defaultColor = "#FFFFFF";
  const { t } = useTranslation();
  const { editor } = useWysiwyg();
  const [color, setColor] = createSignal<string>(defaultColor);

  createEffect(() => {
    const { backgroundColor } = editor().getAttributes("textStyle");
    setColor(isEmpty(backgroundColor) ? defaultColor : backgroundColor);
  });

  return (
    <ColorPicker
      onChange={(value) => setTimeout(() => editor().chain().focus().setBackgroundColor(value).run())}
      value={color()}
      showOpacity
      Trigger={(triggerProps) => (
        <ColorPickerContext>
          {(context) => (
            <ToolbarButton
              {...triggerProps}
              onClick={() => context().setOpen(!context().open)}
              aria-label={t(($) => $.WYSIWYG_COMMAND_BACKGROUND_COLOR_ARIA_LABEL)}
            >
              <Icon name="textBackground" type="outline" size="md" />
              <Icon name="chevronDown" type="solid" size="sm" />
            </ToolbarButton>
          )}
        </ColorPickerContext>
      )}
    />
  );
};

export const LineHeightCommand = () => {
  const { t } = useTranslation();

  return (
    <BaseSelectCommand<string>
      aria-label={t(($) => $.WYSIWYG_COMMAND_LINE_HEIGHT_ARIA_LABEL)}
      icon="lineHeight"
      run={(editor, value) => {
        editor().chain().setLineHeight(value).run();
      }}
      items={[
        { key: "1.0", label: "1.0" },
        { key: "1.2", label: "1.2" },
        { key: "1.4", label: "1.4" },
        { key: "1.5", label: "1.5" },
        { key: "1.6", label: "1.6" },
        { key: "1.8", label: "1.8" },
        { key: "2.0", label: "2.0" },
        { key: "3.0", label: "3.0" },
      ]}
    />
  );
};

export const PlaceholderCommand = () => {
  const { t } = useTranslation();
  const { addPlaceholder } = useWysiwyg();

  return (
    <ToolbarButton
      onClick={addPlaceholder}
      aria-label={t(($) => $.WYSIWYG_COMMAND_PLACEHOLDER_ARIA_LABEL)}
      data-placeholder-button
    >
      <Icon name="lightningBolt" type="solid" size="sm" />
    </ToolbarButton>
  );
};
export const BoldCommand: VoidComponent = () => {
  const { t } = useTranslation();

  return (
    <BaseButtonCommand
      aria-label={t(($) => $.WYSIWYG_COMMAND_BOLD_ARIA_LABEL)}
      icon="bold"
      active={(editor) => editor().isActive("bold")}
      run={(editor) => {
        editor().chain().focus().toggleBold().run();
      }}
    />
  );
};
export const ItalicCommand: VoidComponent = () => {
  const { t } = useTranslation();

  return (
    <BaseButtonCommand
      aria-label={t(($) => $.WYSIWYG_COMMAND_ITALIC_ARIA_LABEL)}
      icon="italic"
      active={(editor) => editor().isActive("italic")}
      run={(editor) => editor().chain().focus().toggleItalic().run()}
    />
  );
};
export const UnderlineCommand: VoidComponent = () => {
  const { t } = useTranslation();

  return (
    <BaseButtonCommand
      aria-label={t(($) => $.WYSIWYG_COMMAND_UNDERLINE_ARIA_LABEL)}
      icon="underline"
      active={(editor) => editor().isActive("underline")}
      run={(editor) => editor().chain().focus().toggleUnderline().run()}
    />
  );
};
export const StrikeThroughCommand: VoidComponent = () => {
  const { t } = useTranslation();

  return (
    <BaseButtonCommand
      aria-label={t(($) => $.WYSIWYG_COMMAND_STRIKE_ARIA_LABEL)}
      icon="strikethrough"
      active={(editor) => editor().isActive("strike")}
      run={(editor) => editor().chain().focus().toggleStrike().run()}
    />
  );
};
export const TextAlignLeftCommand = () => {
  const { t } = useTranslation();
  const { mode } = useWysiwyg();

  return (
    <BaseButtonCommand
      aria-label={t(($) => $.WYSIWYG_COMMAND_TEXT_ALIGN_LEFT_ARIA_LABEL)}
      icon="menuAlt_2"
      active={(editor) => mode() === "wysiwyg" && editor().isActive({ textAlign: "left" })}
      run={(editor) => editor().chain().focus().setTextAlign("left").run()}
    />
  );
};
export const TextAlignCenterCommand = () => {
  const { t } = useTranslation();

  return (
    <BaseButtonCommand
      aria-label={t(($) => $.WYSIWYG_COMMAND_TEXT_ALIGN_CENTER_ARIA_LABEL)}
      icon="centerAlignedText"
      active={(editor) => editor().isActive({ textAlign: "center" })}
      run={(editor) => editor().chain().focus().setTextAlign("center").run()}
    />
  );
};
export const TextAlignRightCommand = () => {
  const { t } = useTranslation();

  return (
    <BaseButtonCommand
      aria-label={t(($) => $.WYSIWYG_COMMAND_TEXT_ALIGN_RIGHT_ARIA_LABEL)}
      icon="menuAlt_3"
      active={(editor) => editor().isActive({ textAlign: "right" })}
      run={(editor) => editor().chain().focus().setTextAlign("right").run()}
    />
  );
};
export const TextJustifyFullCommand = () => {
  const { t } = useTranslation();

  return (
    <BaseButtonCommand
      aria-label={t(($) => $.WYSIWYG_COMMAND_TEXT_JUSTIFY_FULL_ARIA_LABEL)}
      icon="menu"
      active={(editor) => editor().isActive({ textAlign: "justify" })}
      run={(editor) => editor().chain().focus().setTextAlign("justify").run()}
    />
  );
};
export const BulletListCommand = () => {
  const { t } = useTranslation();

  return (
    <BaseButtonCommand
      aria-label={t(($) => $.WYSIWYG_COMMAND_BULLET_LIST_ARIA_LABEL)}
      icon="listBullet"
      active={(editor) => editor().isActive("bulletList")}
      run={(editor) => editor().chain().focus().toggleBulletList().run()}
    />
  );
};

export const OrderedListCommand = () => {
  const { t } = useTranslation();

  return (
    <BaseButtonCommand
      aria-label={t(($) => $.WYSIWYG_COMMAND_ORDERED_LIST_ARIA_LABEL)}
      icon="listNumber"
      active={(editor) => editor().isActive("orderedList")}
      run={(editor) => editor().chain().focus().toggleOrderedList().run()}
    />
  );
};
export const IndentCommand = () => {
  const { t } = useTranslation();

  return (
    <BaseButtonCommand
      aria-label={t(($) => $.WYSIWYG_COMMAND_INDENT_ARIA_LABEL)}
      icon="indent"
      active={() => false}
      run={(editor) => editor().chain().indent().run()}
    />
  );
};
export const OutdentCommand = () => {
  const { t } = useTranslation();

  return (
    <BaseButtonCommand
      aria-label={t(($) => $.WYSIWYG_COMMAND_OUTDENT_ARIA_LABEL)}
      icon="outdent"
      active={() => false}
      run={(editor) => editor().chain().outdent().run()}
    />
  );
};
export const LinkCommand = () => {
  const { t } = useTranslation();
  const { editor } = useWysiwyg();
  const { open } = useModalManager();

  const insertLinkSchema = object({
    url: pipe(
      nonOptional(string(), errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
      url(errorTranslation(keyFromSelector(($) => $.VALIDATION_INVALID_URL))),
    ),
    text: optional(string()),
    openInNewTab: boolean(),
  });

  type Schema = InferOutput<typeof insertLinkSchema>;

  const openInsertLinkModal = (value: Schema) =>
    open({
      content: () => (
        <InputModal<Schema>
          variant="default"
          title={t(($) => $.WYSIWYG_COMMAND_LINK_INSERT_MODAL_TITLE)}
          titleIcon="link"
          schema={insertLinkSchema}
          initialValue={value}
          confirmLabel={t(($) => $.WYSIWYG_COMMAND_LINK_INSERT_MODAL_ACTIONS_CONFIRM)}
          cancelLabel={t(($) => $.WYSIWYG_COMMAND_LINK_INSERT_MODAL_ACTIONS_CANCEL)}
          onConfirm={({ url, text, openInNewTab }) => {
            // Wait a tick until modal is closed, otherwise focus is stolen right away
            setTimeout(() =>
              untrack(() => editor())
                .chain()
                .focus()
                .extendMarkRange("link")
                // Using `.setLink` doesn't work correctly when also adjusting selected text, so we use a lower level API
                .insertContent({
                  type: "text",
                  text: isNil(text) || isEmpty(text) ? url : text,
                  marks: [
                    {
                      type: "link",
                      attrs: { href: url, target: openInNewTab ? "_blank" : "_self" },
                    },
                  ],
                })
                .run(),
            );
          }}
          onCancel={() => {
            // Wait a tick until modal is closed, otherwise focus is stolen right away
            setTimeout(() =>
              untrack(() => editor())
                .chain()
                .focus()
                .run(),
            );
          }}
        >
          {({ value, updateValue, error }) => (
            <div class="flex flex-col gap-6">
              <TextInput
                required
                label={t(($) => $.WYSIWYG_COMMAND_LINK_INSERT_MODAL_INPUT_URL)}
                value={value("url")}
                onBlur={(e) => {
                  updateValue("url", e.target.value);
                }}
                onInput={(e) => {
                  updateValue("url", e.target.value);
                }}
                errorMessage={error("url")}
              />
              <TextInput
                label={t(($) => $.WYSIWYG_COMMAND_LINK_INSERT_MODAL_INPUT_TEXT)}
                value={value("text")}
                onBlur={(e) => {
                  updateValue("text", e.target.value);
                }}
                onInput={(e) => {
                  updateValue("text", e.target.value);
                }}
                errorMessage={error("text")}
              />
              <Checkbox
                label={t(($) => $.WYSIWYG_COMMAND_LINK_INSERT_MODAL_INPUT_TARGET)}
                checked={value("openInNewTab") ?? false}
                setChecked={(value) => {
                  updateValue("openInNewTab", value);
                }}
              />
            </div>
          )}
        </InputModal>
      ),
    });

  return (
    <>
      <BaseButtonCommand
        aria-label={t(($) => $.WYSIWYG_COMMAND_LINK_ARIA_LABEL)}
        icon="link"
        active={() => false}
        run={() => {
          const state = editor().state;
          const { from, to } = state.selection;
          const text = state.doc.textBetween(from, to, " ");

          openInsertLinkModal({ url: "", text, openInNewTab: false });
        }}
      />

      <BubbleMenu
        referenceElement={({ editor, from }) => {
          if (!editor.isActive("link")) {
            return null;
          }

          const dom = editor.view.domAtPos(from);
          const node = dom.node.nodeType === Node.ELEMENT_NODE ? dom.node : dom.node.parentNode;
          if (!isNil(node) && node.nodeName === "A") {
            return node as Element;
          } else {
            return null;
          }
        }}
      >
        <div class="flex items-center gap-1">
          <TextAndIconButton
            text={t(($) => $.WYSIWYG_LINK_TOOLTIP_EDIT_ACTION)}
            icon="externalLink"
            variant="tertiary"
            size="medium"
            onClick={() => {
              const { href, target } = editor().getAttributes("link");
              const { from } = editor().state.selection;
              const dom = editor().view.domAtPos(from);
              const node = dom.node.nodeType === Node.ELEMENT_NODE ? dom.node : dom.node.parentNode;
              if (isNil(node) || node.nodeName !== "A") {
                logger.warn("TipTap selection contains invalid node for 'link' insertion", { node });
                return;
              }

              const text = node.textContent ?? "";
              openInsertLinkModal({ url: href, text, openInNewTab: target === "_blank" });
            }}
          />
          <TextAndIconButton
            text={t(($) => $.WYSIWYG_LINK_TOOLTIP_UNLINK_ACTION)}
            icon="unlink"
            variant="tertiary"
            size="medium"
            onClick={() => {
              editor().chain().unsetLink().focus().run();
            }}
          />
        </div>
      </BubbleMenu>
    </>
  );
};
export const ImageCommand = () => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const { editor } = useWysiwyg();
  const [progress, setProgress] = createSignal<number>(0);
  const [abortController, setAbortController] = createSignal<AbortController>();
  const onDelete = async (file: CustomerResource) => CustomerResourceService.delete(file.id);
  const onCancel = () => {
    abortController()?.abort();
  };
  const onFileSelected = async (file: PendingCustomerResource) =>
    CustomerResourceService.upload(
      file,
      ({ progress }) => setProgress(!isNil(progress) ? progress * 100 : 0),
      setAbortController(new AbortController()).signal,
    );

  const updateImageWidth = (size: string) => {
    const { selection } = editor().state;
    if (isNodeSelection(selection)) {
      const styles = parse(selection.node.attrs.style ?? "")
        .filter((item) => item.type === "declaration")
        .filter((item) => item.property !== "width")
        .map((item) => `${item.property}: ${item.value}`);
      styles.push(`width: ${size}`);

      editor()
        .chain()
        .focus()
        .updateAttributes("image", { style: styles.join("; ") })
        .run();
    }
  };

  return (
    <>
      <BaseButtonCommand
        aria-label={t(($) => $.WYSIWYG_COMMAND_IMAGE_ARIA_LABEL)}
        icon="image"
        active={() => editor().isActive("image")}
        run={() => {
          open({
            content: () => (
              <MediaModal
                defaultTab="library"
                progress={progress()}
                setProgress={setProgress}
                onDelete={onDelete}
                onCancel={onCancel}
                fetchPage={(page, query) => CustomerResourceService.fetchPage(page, { query })}
                mediaUpload={{ onFileSelected }}
                onSelect={(file) => {
                  const src = CustomerResourceService.getPublicUri(file.id);
                  editor().chain().focus().setImage({ src }).run();
                }}
              />
            ),
          });
        }}
      />

      <BubbleMenu
        referenceElement={({ editor, from }) => {
          if (!editor.isActive("image")) {
            return null;
          }

          const node = editor.view.nodeDOM(from);
          return !isNil(node) ? (node as Element) : null;
        }}
      >
        <div class="flex h-full items-center gap-1">
          <For each={["100%", "75%", "50%", "25%", "10%"]}>
            {(size) => (
              <TextButton text={size} variant="tertiary" size="medium" onClick={() => updateImageWidth(size)} />
            )}
          </For>

          <SeparatorLine orientation="vertical" class="h-full" />

          <IconButton
            icon="trash"
            variant="tertiary"
            aria-label={t(($) => $.WYSIWYG_IMAGE_TOOLTIP_DELETE_ACTION)}
            size="medium"
            onClick={() => editor().commands.deleteSelection()}
          />
        </div>
      </BubbleMenu>
    </>
  );
};
export const HorizontalLineCommand = () => {
  const { t } = useTranslation();

  return (
    <BaseButtonCommand
      aria-label={t(($) => $.WYSIWYG_COMMAND_HORIZONTAL_LINE_ARIA_LABEL)}
      icon="minus"
      active={(editor) => editor().isActive("horizontalRule")}
      run={(editor) => editor().chain().focus().setHorizontalRule().run()}
    />
  );
};

export const TableCommand: VoidComponent = () => {
  const { t } = useTranslation();
  const { editor } = useWysiwyg();

  return (
    <>
      <Popover.Root
        positioning={{
          placement: "bottom-start",
        }}
        unmountOnExit
        lazyMount
      >
        <Popover.Context>
          {(context) => (
            <>
              <Popover.Trigger
                asChild={(triggerProps) => (
                  <ToolbarButton
                    {...triggerProps}
                    onClick={() => context().setOpen(!context().open)}
                    aria-label={t(($) => $.WYSIWYG_COMMAND_TABLE_ARIA_LABEL)}
                  >
                    <Icon name="table" type="solid" size="md" />
                    <Icon name="chevronDown" type="solid" size="sm" />
                  </ToolbarButton>
                )}
              />
              <Popover.Positioner>
                <Popover.Content class="z-10 shadow-elevation-light-3">
                  <TableCreation
                    maxCols={10}
                    maxRows={10}
                    onSelect={(cols, rows) => {
                      context().setOpen(false);
                      // Wait a tick until modal is closed, otherwise focus is stolen right away
                      setTimeout(() =>
                        editor()
                          .chain()
                          .focus()
                          .insertTable({
                            cols,
                            rows,
                            withHeaderRow: false,
                          })
                          .updateAttributes("table", {
                            class: "customTable",
                          })
                          .run(),
                      );
                    }}
                  />
                </Popover.Content>
              </Popover.Positioner>
            </>
          )}
        </Popover.Context>
      </Popover.Root>

      <BubbleMenu
        referenceElement={({ editor, from }) => {
          if (!editor.isActive("table")) {
            return null;
          }

          const dom = editor.view.domAtPos(from);
          const node = dom.node.nodeType === Node.ELEMENT_NODE ? dom.node : dom.node.parentNode;
          if (!isNil(node)) {
            const table = (node as Element).closest("table");
            if (!isNil(table)) {
              return table;
            }
          }

          return null;
        }}
      >
        <TextAndIconButton
          text={t(($) => $.WYSIWYG_TABLE_TOOLTIP_DELETE_ACTION)}
          icon="trash"
          variant="tertiary"
          size="medium"
          onClick={() => {
            editor().commands.deleteTable();
          }}
        />
      </BubbleMenu>
    </>
  );
};

type TableCreationProps = {
  maxCols: number;
  maxRows: number;
  onSelect: (cols: number, rows: number) => void;
};
const TableCreation: VoidComponent<TableCreationProps> = (props) => {
  const { t } = useTranslation();
  const keyDownEvent = useKeyDownEvent();
  const [selection, setSelection] = createSignal({ row: 0, col: 0 });
  let ref!: HTMLDivElement;

  const updateSelection = (row: number, col: number) => {
    setSelection({
      row: clamp(row, 1, props.maxRows),
      col: clamp(col, 1, props.maxCols),
    });
  };

  onMount(() => {
    const sizeSelectButtonSize = 18; // size + gap
    const pos = useMousePosition();
    const relative = createPositionToElement(ref, () => pos);
    createEffect(() => {
      updateSelection(Math.ceil(relative.y / sizeSelectButtonSize), Math.ceil(relative.x / sizeSelectButtonSize));
    });
  });

  createEffect(() => {
    const event = keyDownEvent();
    const { row, col } = untrack(() => selection());
    if (isNil(event)) {
      return;
    }

    if (event.key === "ArrowDown") {
      updateSelection(row + 1, col);
    } else if (event.key === "ArrowUp") {
      updateSelection(row - 1, col);
    } else if (event.key === "ArrowRight") {
      updateSelection(row, col + 1);
    } else if (event.key === "ArrowLeft") {
      updateSelection(row, col - 1);
    } else if (event.key === "Enter") {
      untrack(() => {
        props.onSelect(col, row);
      });
    }
  });

  return (
    <div class="z-10 rounded-sm bg-surface-default px-1 py-1.5">
      <div class="flex flex-col gap-0.5" ref={ref}>
        <For each={times(props.maxRows, (i) => i + 1)}>
          {(row) => (
            <div class="flex gap-0.5">
              <For each={times(props.maxCols, (i) => i + 1)}>
                {(col) => {
                  const isActive = createMemo(() => selection().row >= row && selection().col >= col);

                  return (
                    <button
                      class="size-4 cursor-pointer rounded-sm border"
                      classList={{
                        "border-subtle": !isActive(),
                        "border-active bg-surface-active-brand": isActive(),
                      }}
                      aria-label={t(($) => $.WYSIWYG_COMMAND_TABLE_CREATE_BUTTON_ARIA_LABEL, { col, row })}
                      tabIndex={-1}
                      onClick={() => {
                        props.onSelect(col, row);
                      }}
                    />
                  );
                }}
              </For>
            </div>
          )}
        </For>
      </div>
      <div class="mt-1 text-center text-base-regular text-secondary">
        {selection().col}x{selection().row}
      </div>
    </div>
  );
};

export const CodeCommand = () => {
  const { t } = useTranslation();
  const { mode, setMode, codeOnly } = useWysiwyg();

  return (
    <BaseButtonCommand
      icon="code"
      disableOverride={codeOnly()}
      active={() => mode() === "code"}
      run={() => {
        setMode(mode() === "wysiwyg" ? "code" : "wysiwyg");
      }}
      aria-label={t(($) => $.WYSIWYG_COMMAND_CODE_LABEL)}
    />
  );
};
